import React, { PureComponent } from 'react';

import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';

// redux
import { push } from '../../navigation';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { toast } from 'react-toastify';

// Components
import {Button, Dialog} from '@betareader/design-system';
import styled from 'styled-components';

import { accessUtil, authUtil, productCapUtil } from '../../utils';

import PublishingSettings from '../book/detail/publishingSettings';
import SharingWidget from '../../components/sharing/sharingWidget';
import InvitationForm from '../readers/invitationForm';
import ProductLimitMessage from '../../components/standard-messages/productLimitMessage';
import JoinBetaFormDesigner from '../readers/signup-form/joinBetaFormDesigner';

import {
  fetchReaders
} from '../../modules/reader';
import {
  updateCurrentBook
} from '../../modules/book';
import { MinWidthSpan } from '../../style';

const ShareSections = styled.div`
  display: grid;
  gap: var(--br-space-4);
`;

const ShareColumns = styled.div`
  display: grid;
  gap: var(--br-space-4);
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const MenuButton = styled.button.attrs({type: 'button'})`
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  &:focus-visible { outline: 2px solid var(--br-focus); }
`;


export class ShareBookMenuItem extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      openModal: false
    };
  }

  showItem = pathname => pathname !== '/read';

  toggleSharingModal = () => {
    this.setState(prevState => ({
      openModal: !prevState.openModal
    }), () => {
      this.fetchReaders();
    });
  }

  fetchReaders = async () => {
    const idToken = await authUtil.getFreshIdToken();
    const { currentBook } = this.props;
    if (currentBook && accessUtil.isAllowed(currentBook.access, ['edit'])) {
      this.props.fetchReaders(idToken, currentBook._id);
    }
  }

  updateReaderSignupQuestions = async (questions) => {
    const idToken = await authUtil.getFreshIdToken();
    const { currentBook } = this.props;

    // create the survey object if it doesn't exist already, then load it with the fresh questions
    let updatedSurvey;
    if (currentBook.readerSignupSurvey) {
      updatedSurvey = {
        ...currentBook.readerSignupSurvey,
        questions
      };
    } else {
      updatedSurvey = {
        book: currentBook._id,
        questions
      };
    }
    updatedSurvey.placement = 'READER_SIGNUP';
    // save the survey
    fetch(`${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${currentBook._id}/surveys/reader-signup-survey`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'br-token': idToken
      },
      body: JSON.stringify(updatedSurvey)
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(res.error);
        }
        return res.json();
      })
      .then((updatedSurvey) => {
        if (updatedSurvey !== undefined) {
          // now save the book with the new or updated survey
          this.props.updateCurrentBook({
            ...currentBook,
            readerSignupSurvey: updatedSurvey
          })
        }
      })
      .catch((err) => {
        toast.error('SomethingWentWrong');
        console.error(err);
      });
  }

  render() {
    const {
      t, userProfile, activeAccount, location, currentBook, readers
    } = this.props;
    const { openModal } = this.state;

    if (currentBook === undefined
      || currentBook._id === undefined
      || userProfile === undefined
      || !this.showItem(location.pathname)
    ) {
      return null;
    }

    const canAddMoreReaders = productCapUtil.canAddMoreReaders({
      activeAccount, userProfile, readers, currentBook
    });
    const showProductLimitMessage = !canAddMoreReaders
      && currentBook.privacy !== 'unpublished'
      && currentBook.role === 'author';

    return [
      <MenuButton
        className="item"
        key='share-large-screen'
        onClick={this.toggleSharingModal}
      >
        <MinWidthSpan>{currentBook.privacy === 'unpublished' ? t('Publish') : t('Share')}</MinWidthSpan>
      </MenuButton>,
      <Dialog
        key='modal'
        open={openModal}
        onClose={this.toggleSharingModal}
        title={t('Share')}
        actions={<Button variant="secondary" onClick={this.toggleSharingModal}>{t('Close')}</Button>}
      >
          <ShareSections>
            <p>{currentBook.title}</p>
            {
              accessUtil.isAllowed(currentBook.access, ['edit']) && (
                <section>
                    <h3>{t('PrivacyLevel')}</h3>
                    <PublishingSettings
                      key='settings'
                      currentBook={currentBook}
                    />
                </section>
              )
            }
            <ShareColumns>
              {
                currentBook.role === 'author' && (
                  <section>
                    <h3>{t('InviteByEmail')}</h3>
                    <InvitationForm
                      key='email-invitation'
                      disabled={!canAddMoreReaders}
                      invitationSent={() => this.toggleSharingModal()}
                    />
                  </section>
                )
              }
              <section>
                <h3>{t('InviteByLink')}</h3>
                <SharingWidget
                  disabled={['private', 'unpublished'].includes(currentBook.privacy)}
                  buttonSize='tiny'
                  utmCampaign='share-book'
                  url={`${process.env.REACT_APP_SHARE_API_HOST}/link/${currentBook._id}`}
                  message={`${t('Read')} ${currentBook.title} ${t('by')} ${currentBook.authorName}. ${(currentBook.genres || []).join(', ')}. ${currentBook.wordCount.toLocaleString()} ${t('words')}.`}
                  body={`${t('YouShouldBetaRead')} ${currentBook.title} ${t('by')} ${currentBook.authorName} ${t('onBetaReader')} ${process.env.REACT_APP_SHARE_API_HOST}/link/${currentBook._id}?${queryString.stringify({ utm_campaign: 'share-link', utm_source: 'email', utm_medium: 'button' })}`}
                  shareTo={['facebook', 'twitter', 'clipboard']}
                  hashtags={['writingcommunity', 'betareaders', 'amwriting']}
                  infoText={currentBook.privacy === 'private' ? t('SocialSharingIsNotPossibleForPrivateBooks') : null}
                />
              </section>
            </ShareColumns>
            {
              accessUtil.isAllowed(currentBook.access, ['edit']) && (
                <section>
                    <JoinBetaFormDesigner
                      floated='right'
                      // asModal
                      // trigger='Button'
                      size='small'
                      bookTitle={currentBook.title}
                      questions={currentBook.readerSignupSurvey ? currentBook.readerSignupSurvey.questions : []}
                      onUpdate={this.updateReaderSignupQuestions}
                      readOnly={!accessUtil.isAllowed(currentBook.access, ['edit'])}
                    />
                </section>
              )
            }
            {
              showProductLimitMessage && (
                <section>
                    <ProductLimitMessage reachedCap='maxReaders' />
                </section>
              )
            }
          </ShareSections>
      </Dialog>
    ];
  }
}

// redux stuff
const mapStateToProps = state => ({
  activeAccount: state.account.activeAccount,
  userProfile: state.user.userProfile,
  currentBook: state.book.currentBook,
  readers: state.reader.readers
});

const mapDispatchToProps = dispatch => bindActionCreators({
  fetchReaders,
  updateCurrentBook,
  changePage: newPage => push(newPage)
}, dispatch);

export default withTranslation()(withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(ShareBookMenuItem)));
